import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import { useEffect } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { AuthorizationProvider } from "./contexts/AuthorizationContext";
import { AdminAuthorizationProvider } from "./contexts/AdminAuthorizationContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import PrivilegeGate from "./components/PrivilegeGate";
import { RouteErrorBoundary } from "./components/errors/RouteErrorBoundary";
import { AdminErrorBoundary } from "./components/errors/AdminErrorBoundary";

import Auth from "./pages/login/Login";
import Home from "./pages/home/Home";
import PasswordRecovery from "./pages/password-recovery/PasswordRecovery";
import UpdatePassword from "./pages/update-password/UpdatePassword";
import RecoveryUpdatePassword from "./pages/update-password/RecoveryUpdatePassword";
import Catalog from "./pages/catalog/Catalog";
import Organizations from "./pages/organizations/Organizations";
import OrganizationsSearch from "./pages/organizations/OrganizationsSearch";

import InspectionsPage from "./pages/inspections/InspectionsPage";
import InspectionFindingsPage from "./pages/inspections/InspectionFindingsPage";
import Analytics from "./pages/analytics/Analytics";
import AnalyticsDashboards from "./pages/analytics/Dashboards";
import { OrganizationProvider } from "./contexts/OrganizationContext";

import AuthLoadingWrapper from "./components/AuthLoadingWrapper";
import ChallengesPage from "./pages/challenges/ChallengesPage";
import ChallengesManagePage from "./pages/challenges/ChallengesManagePage";
import ChallengeFindingsPage from "./pages/challenges/ChallengeFindingsPage";
import ReportBuilderPage from "./pages/challenges/ReportBuilderPage";
import UserUploaderPage from "./pages/UserUploaderPage";
import LogoUploaderPage from "./pages/LogoUploaderPage";
import LogosFromMapPage from "./pages/LogosFromMapPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import { AdminV2Layout, AdminV2Dashboard } from "./pages/admin-v2";
import ActivityLogPage from "./pages/activity-log/ActivityLogPage";
import ReportsListPage from "./pages/reports/ReportsListPage";
import SyncConfigsPage from "./pages/sync/SyncConfigsPage";
import OrganizationDetailPage from "./pages/admin-v2/organizations/OrganizationDetailPage";
import { TrainingManagementPage, TrainingProgressPage } from "./pages/trainings";
import { ProgramManagementPage } from "./pages/programs";
import "./App.module.scss";

const queryClient = new QueryClient();

function App() {
  // Prevent number inputs from changing value on scroll
  useEffect(() => {
    const handleWheel = (e) => {
      const target = e.target;
      if (
        target.tagName === "INPUT" &&
        target.type === "number" &&
        document.activeElement === target
      ) {
        e.preventDefault();
        e.stopPropagation();
        target.blur();
      }
    };

    // Use capture phase to catch the event before it reaches the input
    document.addEventListener("wheel", handleWheel, {
      capture: true,
      passive: false,
    });
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true });
    };
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
        <Router>
          <AuthProvider>
            <RouteErrorBoundary>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Auth />} />
                <Route path="/password-recovery" element={<PasswordRecovery />} />
                <Route path="/recovery-update-password" element={<RecoveryUpdatePassword />} />

                {/* Update password route */}
                <Route path="/update-password" element={<UpdatePassword />} />

                {/* Admin V2 routes - CON AdminAuthorizationProvider */}
                <Route
                  path="/admin-v2"
                  element={
                    <AuthLoadingWrapper>
                      <AdminAuthorizationProvider>
                        <AdminProtectedRoute>
                          <AdminErrorBoundary>
                            <AdminV2Layout />
                          </AdminErrorBoundary>
                        </AdminProtectedRoute>
                      </AdminAuthorizationProvider>
                    </AuthLoadingWrapper>
                  }
                >
                  <Route index element={<AdminV2Dashboard />} />
                  <Route path="assignments" element={<AdminV2Dashboard />} />
                  <Route path="users" element={<AdminV2Dashboard />} />
                  <Route path="organizations/:id" element={<OrganizationDetailPage />} />
                </Route>

                {/* Protected routes CON OrganizationProvider */}
                <Route
                  path="/"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <OrganizationsSearch />
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/analytics"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <Analytics />
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/analytics/dashboards"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="READ_ANALYTICS">
                                <AnalyticsDashboards />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/activity-log"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="READ_ACTIVITY_FEED">
                                <ActivityLogPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/catalog"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <Catalog />
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/organizations"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <Organizations />
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/organizations/search"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <OrganizationsSearch />
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/inspections"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <InspectionsPage />
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/inspections/:inspectionId/findings"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <InspectionFindingsPage />
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/challenges/report-builder"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="CREATE_REPORT_TEMPLATE">
                                <ReportBuilderPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/reports"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="READ_REPORT">
                                <ReportsListPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/sync-configs"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="READ_SYNC_CONFIG">
                                <SyncConfigsPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/challenges/:challengeId/findings"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <ChallengeFindingsPage />
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/challenges/manage"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="CREATE_INSPECTION">
                                <ChallengesManagePage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/challenges"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="CREATE_INSPECTION">
                                <ChallengesPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                {/* Training routes */}
                <Route
                  path="/trainings/manage"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="CREATE_TRAINING">
                                <TrainingManagementPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                <Route
                  path="/trainings/progress"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="READ_TRAINING_CONTENT_PROGRESS">
                                <TrainingProgressPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                {/* Program routes */}
                <Route
                  path="/programs/manage"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="CREATE_PROGRAMS_VERSION">
                                <ProgramManagementPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                {/* Route for User Uploader */}
                <Route
                  path="/user-uploader"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="CREATE_ORGANIZATION">
                                <UserUploaderPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                {/* Route for Logo Uploader */}
                <Route
                  path="/logo-uploader"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="UPDATE_ORGANIZATION">
                                <LogoUploaderPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                {/* Route for Logos From Map */}
                <Route
                  path="/logos-from-map"
                  element={
                    <AuthLoadingWrapper>
                      <ProtectedRoute>
                        <OrganizationProvider>
                          <AuthorizationProvider>
                            <Home>
                              <PrivilegeGate privilege="UPDATE_ORGANIZATION">
                                <LogosFromMapPage />
                              </PrivilegeGate>
                            </Home>
                          </AuthorizationProvider>
                        </OrganizationProvider>
                      </ProtectedRoute>
                    </AuthLoadingWrapper>
                  }
                />

                {/* Unauthorized page */}
                <Route
                  path="/unauthorized"
                  element={
                    <AuthLoadingWrapper>
                      <UnauthorizedPage />
                    </AuthLoadingWrapper>
                  }
                />

                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </RouteErrorBoundary>
          </AuthProvider>
        </Router>
      </SnackbarProvider>
    </QueryClientProvider>
  );
}

export default App;
